import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import type { ClassicEditor } from 'ckeditor5'
import { elementToEditorData } from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useTreeMutationsWithTrackChanges } from 'components/WorkspaceEditor/components/TrackChangesPanel/services'
import { hasValueTextAfterFieldRef } from 'components/WorkspaceEditor/features/drawingDesignation/utils/designationEditUtils'
import { useInsertPillAtCursor } from 'components/WorkspaceEditor/features/pillSelection/hooks/useInsertPillAtCursor'
import {
    activeEditorRegistry,
    unsavedParagraphDraft,
} from 'components/WorkspaceEditor/features/promoteDemote'
import { useNewContentFocus } from 'components/WorkspaceEditor/features/scrollToNewContent/hooks/useNewContentFocus'
import { usePlainTextUOMInsert } from 'components/WorkspaceEditor/features/uomInsert'
import {
    useCKEditorToggle,
    useContentEditorFocus,
} from 'components/WorkspaceEditor/hooks'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { DataNode } from 'components/WorkspaceEditor/types'
import {
    isProductTypeOrCopy,
    nullOrUndefinedResolver,
} from 'components/WorkspaceEditor/utils'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { useShallowSelector } from '../../../../Workspace2Store'
import { useNewContentEditing } from '../../../../features/scrollToNewContent'
import type {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../../../ContentEditor'
import { useKeyedText } from './useKeyedText'
import { usePlainTextContentMutation } from './usePlainTextContentMutation'
import { usePlainTextHandlers } from './usePlainTextHandlers'
import { useSaveAwareEditorActivation } from './useSaveAwareEditorActivation'

/**
 * Defines the structure for CKEditor event handlers used in the plain text controller.
 */
interface PlainTextHandlers {
    /** Handler triggered when the CKEditor instance is ready. Used to store the editor reference for potential rollbacks. */
    onReady: ClassicEditorOnReadyHandler
    /** Handler triggered when the CKEditor instance loses focus. Handles saving changes via optimistic updates. */
    onBlur: ClassicEditorHandler
    /** Debounced handler triggered when the CKEditor content changes. Calculates and stores unsaved changes. */
    onChange: ClassicEditorHandler
    /** Handler triggered when the CKEditor instance gains focus. Handles setting the editor to active state. */
    onFocusCapture: (event?: React.FocusEvent<HTMLElement>) => void
    /** Handler triggered when the text is clicked. Captures click coordinates for cursor positioning. */
    onClick: (event: React.MouseEvent<HTMLElement>) => void
    /** Handler triggered when mouse moves over the text. Tracks cursor position for positioning. */
    onMouseMove: (event: React.MouseEvent<HTMLElement>) => void
    /** Handler triggered when a key is pressed. Uses onKeyDown for Enter/Save, onKeyUp for Escape. */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
    /** Handler triggered when a key is released. Optimized for Escape key reliability. */
    onKeyUp: (event: React.KeyboardEvent<HTMLElement>) => void
    /** Handler fired after ContentEditor's configureEditor() completes. Signals mount is truly complete. */
    onConfigComplete: () => void
}

/** Returns true when a changeType represents a user-introduced change (not base content). */
const isUserIntroducedChange = (changeType?: string | null): boolean => {
    const ct = changeType?.toUpperCase()
    return ct === 'ADDED' || ct === 'ACCEPTED'
}

/**
 * Custom hook to manage the state and interactions for a plain text content item
 * using CKEditor within the workspace. It handles editor activation, optimistic updates,
 * and synchronization with the workspace tree state.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @param {DataNode} [node] - The parent node with contentItemView containing parent elements. Also used for MFR/BOD checking and determining whether content is user-added (suppresses key modification warnings).
 * @returns An object containing state, handlers, and derived values for the plain text editor.
 */
export const usePlainTextController = (
    contentItem: Partial<TextContentElement>,
    node?: DataNode
) => {
    const textElement = useMemo(() => nullOrUndefinedResolver(contentItem), [
        contentItem,
    ])
    // Captured once and held for the whole mount: CKEditor wipes its content whenever
    // the `data` prop changes, so this must not go back to empty mid-mount.
    const restoredDraftRef = useRef(
        unsavedParagraphDraft.restore(contentItem.id)
    )
    const [unsavedData, setUnsavedData] = useState<string>(
        () => restoredDraftRef.current?.unsavedData ?? ''
    )
    const formattedContentId = useMemo(
        () => `plain-text-${textElement.id || 'default'}`,
        [textElement.id]
    )

    const { isContentTeamFromStudioPage } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
    }))

    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

    const { registerEditorRef, editorRef } = useTransactionManager()
    const [shouldTriggerNewContent, setShouldTriggerNewContent] = useState(
        false
    )
    const [clickCoordinates, setClickCoordinates] = useState<
        { x: number; y: number } | undefined
    >()

    const {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        scheduleInsertAfterSave,
    } = usePlainTextContentMutation({
        setUnsavedData,
        setShouldTriggerNewContent,
    })

    const paragraphNodeId = node?.contentItemView?.id
    const isParagraphUnsaved =
        !!paragraphNodeId && !node?.contentItemView?.dtvItemId

    // For keyed text parsing, only the FIRST element should use parent contentItemView
    // This prevents duplicate rendering of the full keyed text on every element
    const isFirstElement = Boolean(
        node?.contentItemNodes?.[0]?.id === contentItem.id
    )

    // Skip contentItemView if first element is FieldRef (Bug 2574397)
    // DesignationField handles display and shouldHideElement hides the duplicate elements.
    const firstElementIsFieldRef =
        node?.contentItemNodes?.[0]?.type === 'FieldRef'

    // Lock the first element as a key for Product Types and PT copies.
    const isPTKey = Boolean(isProductTypeOrCopy(node) && isFirstElement)

    // Only aggregate contentItemView when the key will be locked (isPTKey=true).
    // When the key is editable (non-PT), each element must render independently
    // to preserve correct element IDs in the save payload.
    const shouldUseContentItemView =
        isFirstElement &&
        node?.contentItemView &&
        !firstElementIsFieldRef &&
        isPTKey

    const contentItemForKeyedText =
        shouldUseContentItemView && node?.contentItemView
            ? node.contentItemView
            : contentItem

    // Determine if this non-first element is after the key delimiter (colon).
    // Elements after the colon are value content and should not be treated as keyed text.
    const isAfterKeyDelimiter = useMemo(() => {
        if (isFirstElement) return false
        if (!node?.contentItemNodes || !contentItem.id) return false

        const currentIdx = node.contentItemNodes.findIndex(
            (n) => n.id === contentItem.id
        )
        // Element not in contentItemNodes (e.g. the EmptyPlainTextContainer's
        // additional element) — it is value content by definition, so treat it
        // as after the key delimiter to suppress spurious key-modification warnings.
        if (currentIdx === -1) return true
        if (currentIdx === 0) return false

        for (let i = 0; i < node.contentItemNodes.length; i++) {
            const n = node.contentItemNodes[i]
            if (n.type === 'OptionGroup' || n.type === 'FieldRef') continue
            // Skip user-added/accepted colons — consistent with the nodeHasKeyedText
            // detection above, which also treats these as non-key-delimiter colons.
            if (isUserIntroducedChange(n.changeType)) continue
            if (n.text?.includes(':')) {
                return currentIdx > i
            }
        }

        return false
    }, [isFirstElement, node?.contentItemNodes, contentItem.id])

    const {
        keyedText,
        keyedTextValue,
        isKeyedText,
        hasDesignationFormat,
        prependKeyedTextElement,
    } = useKeyedText(
        contentItemForKeyedText,
        isContentTeamFromStudioPage,
        node,
        isAfterKeyDelimiter
    )

    const hasValueText = useMemo(
        () => hasValueTextAfterFieldRef(node?.contentItemNodes),
        [node?.contentItemNodes]
    )

    // Detect if this element is part of a split key
    // A split key occurs when the key is distributed across multiple elements
    // and this element is the first one (containing the start of the key) but doesn't have the colon
    const isSplitKeyElement = Boolean(
        isKeyedText && isFirstElement && !textElement.text?.includes(':')
    )

    // Determine if the key was stripped from CKEditor (EditorContent only provides value).
    // This applies both to normal locked keys and to OM/content-team excluded content that
    // still has a real colon-delimited key (EditorContent strips the key for both cases).
    const shouldPrependKeyOnSave = isPTKey && keyedTextValue !== undefined

    // Analyze key-portion elements (up to and including the colon element) to determine:
    // 1. isBaseContentKey: whether the key contains any base content (non-ADDED elements)
    // 2. hasAcceptedKeyChanges: whether any key change was accepted (base key is dirty)
    // 3. nodeHasKeyedText: whether the node contains keyed text (stable across renders)
    // 4. nodeKeyedText: the concatenated key text from node elements (used as fallback in confirmation dialog)
    const {
        isBaseContentKey,
        hasAcceptedKeyChanges,
        nodeHasKeyedText,
        nodeKeyedText,
    } = useMemo(() => {
        // User-added content items are never base content — skip the warning entirely
        if (node?.isUserAdded) {
            return {
                isBaseContentKey: false,
                hasAcceptedKeyChanges: false,
                nodeHasKeyedText: false,
                nodeKeyedText: '',
            }
        }

        const fallback = {
            isBaseContentKey: contentItem.changeType?.toUpperCase() !== 'ADDED',
            hasAcceptedKeyChanges: false,
            nodeHasKeyedText: false,
            nodeKeyedText: '',
        }
        if (!node?.contentItemNodes) return fallback

        let hasBaseContent = false
        let hasAccepted = false
        let foundColon = false
        let keyText = ''

        for (const item of node.contentItemNodes) {
            const ct = item.changeType?.toUpperCase()
            if (ct === 'ACCEPTED') hasAccepted = true
            if (ct !== 'ADDED') hasBaseContent = true
            keyText += item.text || ''
            if (item.text?.includes(':')) {
                // If the colon is in a user-added or accepted element, the colon
                // was introduced by the user (e.g., ": new value") — not a base
                // content key, so skip the key-modification warning.
                if (!isUserIntroducedChange(item.changeType)) {
                    foundColon = true
                }
                break
            }
        }

        // No colon found — not keyed text, use element-level fallback
        if (!foundColon) return fallback

        return {
            isBaseContentKey: hasBaseContent,
            hasAcceptedKeyChanges: hasAccepted,
            nodeHasKeyedText: true,
            nodeKeyedText: keyText,
        }
    }, [node?.isUserAdded, node?.contentItemNodes, contentItem.changeType])

    const initialData = useMemo(
        () =>
            restoredDraftRef.current?.html ?? elementToEditorData(textElement),
        [textElement]
    )

    const ckeToggle = useCKEditorToggle(formattedContentId)

    // Use the centralized save-aware editor activation hook
    const { handleClickWithSaveSupport } = useSaveAwareEditorActivation({
        ckeToggle,
        setClickCoordinates,
    })

    const shouldRenderContentEditor = useMemo(
        () =>
            ckeToggle.active ||
            (Boolean(unsavedData) && unsavedData !== textElement.text),
        [ckeToggle.active, unsavedData, textElement.text]
    )

    const { editorFocusRef } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
        controlType: 'plainText',
    })
    // Separate from editorFocusRef, which unFocusEditor nulls on every save —
    // unregister needs the exact instance it registered, not whatever's focused now.
    const registeredEditorRef = useRef<ClassicEditor>()

    const { registerNewEditor, unFocusEditor } = useNewContentFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
    })

    const [hasDeletedWholeText, setHasDeletedWholeText] = useState(false)
    const { startEditing } = useNewContentEditing()
    const onInsertPillRef = useRef<(() => void) | undefined>(undefined)
    const onInsertUOMRef = useRef<(() => void) | undefined>(undefined)
    const skipNextBlurRef = useRef(false)

    // Extract all handler logic into a single hook
    const {
        handleOnBlur,
        handleOnFocusCapture,
        handleOnClick,
        handleOnMouseMove,
        handleOnKeyDown,
        handleOnKeyUp,
        debouncedOnChange,
        initialSuggestions,
        processEditorContent,
    } = usePlainTextHandlers({
        textElement,
        contentItem,
        unsavedData,
        hasDeletedWholeText,
        setUnsavedData,
        setHasDeletedWholeText,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        prependKeyedTextElement,
        editorRef,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        startEditing,
        registerNewEditor,
        registerEditorRef,
        ckeToggle,
        setClickCoordinates,
        isSplitKeyElement,
        keyedText,
        isKeyedText,
        isBaseContentKey,
        hasAcceptedKeyChanges,
        nodeHasKeyedText,
        nodeKeyedText,
        shouldPrependKeyOnSave,
        isAfterKeyDelimiter,
        onInsertPillRef,
        onInsertUOMRef,
        skipNextBlurRef,
    })

    // Compose handlers object with enhanced wrapper handler
    const handlers: PlainTextHandlers = useMemo(
        () => ({
            onReady: (editor) => {
                registerNewEditor(editor)
                // register the CKEditor instance to use in Transaction Manager (optimistic update)
                registerEditorRef(editor)
                // register the editor for focus management
                editorFocusRef.current = editor
                // lets promote/demote refocus via the editor, not the DOM (which saves)
                activeEditorRegistry.register(contentItem.id, editor)
                registeredEditorRef.current = editor
            },
            onBlur: handleOnBlur,
            onChange: debouncedOnChange,
            onFocusCapture: handleOnFocusCapture,
            onClick: (event) =>
                handleClickWithSaveSupport(event, handleOnClick),
            onMouseMove: handleOnMouseMove,
            onKeyDown: handleOnKeyDown,
            onKeyUp: handleOnKeyUp,
            onConfigComplete: ckeToggle.markMountComplete,
        }),
        [
            registerNewEditor,
            registerEditorRef,
            editorFocusRef,
            handleOnBlur,
            debouncedOnChange,
            handleOnFocusCapture,
            handleClickWithSaveSupport,
            handleOnClick,
            handleOnMouseMove,
            handleOnKeyDown,
            handleOnKeyUp,
            ckeToggle,
            contentItem.id,
        ]
    )

    useEffect(
        () => () => {
            if (registeredEditorRef.current)
                activeEditorRegistry.unregister(
                    contentItem.id,
                    registeredEditorRef.current
                )
        },
        [contentItem.id]
    )

    const canEnableInsertPill =
        isCurrentlyOnOMWorkspace && node?.type !== 'Article'

    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()

    const insertPillAtCursor = useInsertPillAtCursor({
        editorRef,
        skipNextBlurRef,
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor: ckeToggle.deactivate,
        processEditorContent,
    })

    // Keep the ref in sync so the Ctrl+Alt+P shortcut always calls the latest version.
    // useLayoutEffect avoids the double-mutation that Strict Mode causes when
    // setting refs directly in the render body.
    useLayoutEffect(() => {
        onInsertPillRef.current = canEnableInsertPill
            ? insertPillAtCursor
            : undefined
    }, [canEnableInsertPill, insertPillAtCursor])

    useEffect(() => {
        if (!canEnableInsertPill) return
        const handler = ckeToggle.active ? insertPillAtCursor : undefined
        setReactiveWorkspaceActions({
            isInsertPill: ckeToggle.active,
            omWorkspaceInsertPillHandler: handler,
        })
        return () => {
            if (
                handler &&
                reactiveWorkspaceActions().omWorkspaceInsertPillHandler ===
                    handler
            ) {
                setReactiveWorkspaceActions({
                    omWorkspaceInsertPillHandler: undefined,
                })
            }
        }
    }, [ckeToggle.active, canEnableInsertPill, insertPillAtCursor])

    usePlainTextUOMInsert({
        editorRef,
        skipNextBlurRef,
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor: ckeToggle.deactivate,
        processEditorContent,
        scheduleInsertAfterSave,
        handleAddContentExecuteOptimisticWithFallback,
        isCurrentlyOnOMWorkspace,
        nodeType: node?.type,
        isParagraphUnsaved,
        onInsertUOMRef,
        ckeToggleActive: ckeToggle.active,
    })

    return {
        text: textElement.text,
        formattedContentId,
        initialData,
        initialSuggestions,
        clickCoordinates,
        ckeToggle,
        shouldRenderContentEditor,
        isKeyedText,
        hasDesignationFormat,
        keyedText,
        keyedTextValue,
        hasValueTextAfterFieldRef: hasValueText,
        isContentTeamFromStudioPage,
        handlers,
    }
}
